import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Button, message } from "antd";
import TaskList from "../components/TaskList";
import TaskModal from "../components/TaskModal";
import { useTasks } from "../hooks/useTasks";
import Loader from "../../../../components/Common/loader";
import { ClassTask } from "../../../../types/classTaskTypes";
import { useSelector } from "react-redux";
import TableFilter, { FilterField } from "../../../../components/Common/TableFilter/TableFilter";
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import AttachmentPreviewModal from "../components/AttachmentPreviewModal";

const TeacherTaskPage: React.FC = () => {
  const teacherId = useSelector((state: any) => state.authSlice.user?.roleId);
  const [isModalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<Partial<ClassTask> | null>(null);
  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);

  const { tasks, loading, deleteTask, createTask, updateTask, fetchTasks } = useTasks({ teacherId });
  const { t } = useTranslation();

  // Define filter fields
  const filterFields: FilterField[] = [
    { key: "title", label: t("task_title"), type: "text" },
    { key: "subject", label: t("subject"), type: "text" },
    { key: "dueDate", label: t("due_date"), type: "date" },
  ];

  // Apply filters
  const handleApplyFilters = (newFilters: { [key: string]: string | number | null }) => {
    setFilters(newFilters);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({});
  };

  // Filter tasks dynamically
  const filteredTasks = tasks.filter((task) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      return String(task[key as keyof ClassTask]).toLowerCase().includes(String(value).toLowerCase());
    })
  );

  // Handle Edit Task
  const handleEditTask = (taskId: string) => {
    const taskToEdit = tasks.find((task) => task.id === taskId);
    if (!taskToEdit) {
      message.error(t("task_not_found"));
      return;
    }
    setEditingTask(taskToEdit);
    setModalVisible(true);
  };

  // ✅ Handle Delete Task
  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      message.success(t("task_deleted_successfully"));
      fetchTasks({ teacherId }); // Refresh tasks
    } catch (error) {
      message.error(t("failed_to_delete_the_task"));
    }
  };

  // ✅ Handle Create/Update Task Submission
  const handleSubmitTask = async (formData: FormData) => {
    try {
      let res = false;
      if (editingTask) {
        res = await updateTask(editingTask.id!, formData);
      } else {
        formData.append("teacherId", teacherId);
        res = await createTask(formData);
      }
      if (res) {
        fetchTasks({ teacherId });
        setModalVisible(false);
        setEditingTask(null);
      }
      return res;
    } catch (error) {
      console.error("Error saving task:", error);
      return false;
    }
  };
  const breadcrumbItems = [
    { label: "dashboard", path: "/dashboard" },
    { label: "home_work", active: true },
  ];

  const handleShowAttachments = (task: ClassTask) => {
    const urls: string[] = task.attachments ?? []
    setAttachmentUrls(urls);
    setAttachmentModalVisible(true);
  };

  // if (loading) return <Loader />;

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("home_work")}
          breadcrumbItems={breadcrumbItems}
        />
        <div className="d-flex mb-3 justify-content-end gap-2">
          <CustomButton
            className="btn btn-primary"
            onClick={() => {
              setEditingTask(null);
              setModalVisible(true);
            }}
          >
            <i className="ti ti-plus me-2" />
            {t("create_task")}
          </CustomButton>
          <TableFilterDynamique
            fields={filterFields} filters={filters} onApply={handleApplyFilters} onReset={handleResetFilters}
          />
        </div>

        <TaskList loading={loading} tasks={filteredTasks} onEdit={handleEditTask} onDelete={handleDeleteTask} onShowAttachments={handleShowAttachments} />


        <TaskModal
          visible={isModalVisible}
          onClose={() => setModalVisible(false)}
          onSubmit={handleSubmitTask}
          teacherId={teacherId}
          initialValues={editingTask || undefined}
        />


        <AttachmentPreviewModal
          visible={attachmentModalVisible}
          onClose={() => setAttachmentModalVisible(false)}
          attachments={attachmentUrls}
        />
      </div>
    </div>
  );
};

export default TeacherTaskPage;
